import AtomModel from "./AtomModel";
import BondModel from "./BondModel";
import AtomContainer from "./AtomContainer";
import IPosition from "./IVector";
import AtomChirality from './AtomChirality';
import IAtomModel from "./IAtomModel";
import EncoderOptions from "./EncoderOptions";
import IBondModel from "./IBondModel";

export default class EncoderUtil {
    static DEFAULT_OPTIONS : EncoderOptions = {
        encodePosition: true,
        encodeChirality: true,
        encodeBondTypes: true,
        encodeGeometricIsomer: true
    }

    static options : EncoderOptions = {...EncoderUtil.DEFAULT_OPTIONS}

    static updateEncoderOptions = (opts: EncoderOptions) => EncoderUtil.options = {...EncoderUtil.options, ...opts}
    static encodeAtoms(atoms: IAtomModel[]): string {
        const symbols = atoms.map(atom => {
            if (atom.chirality && EncoderUtil.options.encodeChirality) {
                const record = [
                    atom.symbol,
                    atom.chirality === AtomChirality.R ? "r" : "s"
                ];
                return record.join(":");
            } else {
                return atom.symbol
            }
        });
        return symbols.join(',');
    }

    static encodeLonePairs(atoms: IAtomModel[]): string {
        const pairs: string[] = [];
        atoms.forEach((atom, idx) => {
            if (atom.lonePairs !== 0) {
                pairs.push(EncoderUtil.encodeIndexedValue(idx, atom.lonePairs))
            }
        })
        return pairs.join(',');
    }

    static encodeRadicals(atoms: IAtomModel[]): string {
        const pairs: string[] = [];
        atoms.forEach((atom, idx) => {
            if (atom.radicals !== 0) {
                pairs.push(EncoderUtil.encodeIndexedValue(idx, atom.radicals))
            }
        })
        return pairs.join(',');
    }

    static encodeIndexedValue(idx: number, value: number): string {
        const record = [idx, value];
        return record.join(':');
    }

    static encodeCharges(atoms: IAtomModel[]): string {
        const charges: string[] = [];
        atoms.forEach((atom, idx) => {
            if (atom.charge !== 0) {
                charges.push(EncoderUtil.encodeIndexedValue(idx, atom.charge))
            }
        })
        return charges.join(',');
    }

    static encodeBond(bond: IBondModel, atoms: IAtomModel[]): string {
        const record: any[] = [atoms.indexOf(bond.a1), atoms.indexOf(bond.a2), bond.order];
        // PIPH-269: slot 4 is character-discriminated. Wedge/dash (l/w/d) only apply to
        // single bonds; E/Z only applies to double bonds — they're mutually exclusive,
        // so they share slot 4 without ambiguity.
        if (bond.order === 2 && bond.isomerConfig && EncoderUtil.options.encodeGeometricIsomer) {
            record.push(bond.isomerConfig);
        } else if (bond.type && EncoderUtil.options.encodeBondTypes) {
            record.push(bond.type);
        }
        return record.join(':');
    }

    static encodeBonds(bonds: IBondModel[], atoms: IAtomModel[]): string {
        const record = bonds.map(bond => EncoderUtil.encodeBond(bond, atoms));
        return record.join(',');
    }

    // useBasicConfiuration is for backwards compatibility. It will be deprecated in the
    // future.
    static encodeAtomContainer(mol: AtomContainer, useBasicConfiguration: boolean = false): string {
        const parts = [
            EncoderUtil.encodeAtoms(mol.atoms),
            EncoderUtil.encodeLonePairs(mol.atoms),
            EncoderUtil.encodeCharges(mol.atoms),
            EncoderUtil.encodeBonds(mol.bonds, mol.atoms),
        ];
        if(!useBasicConfiguration) parts.push(EncoderUtil.encodeRadicals(mol.atoms));

        const hasPositions = mol.atoms.some(atom => atom.pos);
        if (hasPositions && EncoderUtil.options.encodePosition) {
            const points = mol.atoms.map(atom => atom.pos ? atom.pos : {x: NaN, y: NaN});
            const pointString = EncoderUtil.encodePoints(points);
            parts.push(pointString);
        }

        if (!useBasicConfiguration) {
            const exConfig = [];
            if (parts[0].length) exConfig.push("a*" + parts[0]);
            if (parts[1].length) exConfig.push("lp*" + parts[1]);
            if (parts[2].length) exConfig.push("c*" + parts[2]);
            if (parts[3].length) exConfig.push("b*" + parts[3]);
            if (parts[4].length) exConfig.push("rad*" + parts[4]);
            if (parts[5] && parts[5].length) exConfig.push("p*" + parts[5]);
            return exConfig.join('|');
        } else {
            return parts.join('|');
        }
    }

    // DECODE
    static decodeAtoms(atoms: string): AtomModel[] {
        let models: AtomModel[] = [];
        if (atoms) {
            const symbols = atoms.split(',');
            models = symbols.map(a => {
                const p = a.split(":");
                const atom = new AtomModel(p[0]);
                if (p.length > 1) {
                    const c = p[1].toLowerCase();
                    if (c === "s") {
                        atom.chirality = AtomChirality.S;
                    } else if (c) {
                        atom.chirality = AtomChirality.R;
                    }
                }
                return atom;
            });
        }
        return models;
    }

    static decodeIndexedValue(val: string): string[] {
        const values = val.split(':');
        return values;
    }

    static decodeLonePairs(lps: string, atoms: IAtomModel[]) {
        const pairs = lps.split(',');
        pairs.forEach(pair => {
            const iv = EncoderUtil.decodeIndexedValue(pair);
            const idx = Number(iv[0]);
            const val = Number(iv[1]);
            if (idx >= 0 && idx < atoms.length) {
                atoms[idx].lonePairs = val;
            }
        })
    }

    static decodeRadicals(radicals: string, atoms: IAtomModel[]) {
        const rads = radicals.split(',');
        rads.forEach(rad => {
            const iv = EncoderUtil.decodeIndexedValue(rad);
            const idx = Number(iv[0]);
            const val = Number(iv[1]);
            if (idx >= 0 && idx < atoms.length) {
                atoms[idx].radicals = val;
            }
        })
    }

    static decodeCharges(charges: string, atoms: IAtomModel[]) {
        const charge = charges.split(',');
        charge.forEach(pair => {
            const iv = EncoderUtil.decodeIndexedValue(pair);
            const idx = Number(iv[0]);
            const val = Number(iv[1]);
            if (idx >= 0 && idx < atoms.length) {
                atoms[idx].charge = val;
            }
        })
    }

    static decodeBonds(bondString: string, atoms: IAtomModel[]): BondModel[] {
        const bondValues = bondString.split(',');
        const bonds = bondValues.map(bv => EncoderUtil.decodeBond(bv, atoms));
        return bonds;
    }

    static decodeBond(bv: string, atoms: IAtomModel[]): BondModel {
        const iv = EncoderUtil.decodeIndexedValue(bv);
        const a1 = Number(iv[0]);
        const a2 = Number(iv[1]);
        const o = Number(iv[2]);
        // PIPH-269: slot 4 is character-discriminated. `e`/`z` → isomerConfig (alkene
        // geometry); anything else (`l`/`w`/`d`/`/`/`\\`) → bond display type. The two
        // are mutually exclusive by chemistry, so this resolves cleanly.
        let type: string | undefined;
        let isomerConfig: 'e' | 'z' | undefined;
        if (iv.length > 3) {
            const tok = iv[3].toLowerCase();
            if (tok === 'e' || tok === 'z') {
                isomerConfig = tok;
            } else {
                type = iv[3];
            }
        }
        const bond = new BondModel(atoms[a1], atoms[a2], o, type);
        if (isomerConfig) bond.isomerConfig = isomerConfig;
        return bond;
    }

    static isExtendedConfiguration(config: string): boolean {
        return config[0] >= 'a' && config[0] <= 'z'
    }

    static decodeAtomContainer(containerString: string): AtomContainer {
        const parts = containerString.split('|');

        if (this.isExtendedConfiguration(containerString)) {
            let atoms = "";
            let lonePairs = "";
            let radicals = "";
            let charges = "";
            let bonds = "";
            let positions = "";

            parts.forEach(part => {
                const subParts = part.split("*");
                if (subParts[0].toLowerCase() === "a") atoms = subParts[1];
                else if (subParts[0].toLowerCase() === "lp") lonePairs = subParts[1];
                else if (subParts[0].toLowerCase() === "rad") radicals = subParts[1];
                else if (subParts[0].toLowerCase() === "c") charges = subParts[1];
                else if (subParts[0].toLowerCase() === "b") bonds = subParts[1];
                else if (subParts[0].toLowerCase() === "p") positions = subParts[1];
            })
            return EncoderUtil.decodeAtomContainerFromParts(atoms, lonePairs, radicals, charges, bonds, positions);
        } else {
            return EncoderUtil.decodeAtomContainerFromParts(parts[0], parts[1], "", parts[2], parts[3], parts[4]);
        }
    }

    static decodeAtomContainerFromParts(atoms: string, lonePairs: string, radicals: string, charges: string, bonds: string, positions?: string): AtomContainer {
        const container = new AtomContainer();
        container.atoms = this.decodeAtoms(atoms);
        if (lonePairs) {
            this.decodeLonePairs(lonePairs, container.atoms);
        }
        if (radicals) {
            this.decodeRadicals(radicals, container.atoms);
        }
        if (charges) {
            this.decodeCharges(charges, container.atoms);
        }
        if (bonds)
            container.bonds = this.decodeBonds(bonds, container.atoms);

        if (positions) {
            const points = this.decodePoints(positions);
            points.forEach((pt, idx) => {
                container.atoms[idx].pos = pt
            });

        }
        return container;
    }

    static encodePoints2(pts: IPosition[]): string {
        const points = pts.map((pt) =>
            [
                this.encodeNumberFixed(pt.x),
                this.encodeNumberFixed(pt.y)
            ].join(':'));
        return points.join(',');
    }

    static encodePoints3(pts: IPosition[]): string {
        const points = pts.map((pt) =>
            [
                this.encodeNumberFixed(pt.x),
                this.encodeNumberFixed(pt.y),
                pt.z ? this.encodeNumberFixed(pt.z) : 0
            ].join(':'));
        return points.join(',');
    }

    static encodeNumberFixed(num: any): number {
        return parseInt(num) === num ? num : num.toFixed(2);
    }

    static encodePoints(pts: IPosition[]): string {
        const has3d = pts.some(pt => pt.z);
        if (has3d) {
            return EncoderUtil.encodePoints3(pts);
        } else {
            return EncoderUtil.encodePoints2(pts);
        }
    }

    static decodePoints(pointString: string): IPosition[] {
        let points: IPosition[] = [];
        if (pointString) {
            const pts = pointString.split(',');
            points = pts.map(pt => {
                const ptData = EncoderUtil.decodeIndexedValue(pt);
                if (ptData[2]) {
                    return {x: Number(ptData[0]), y: Number(ptData[1]), z: Number(ptData[2])};
                } else {
                    return {x: Number(ptData[0]), y: Number(ptData[1])}
                }
            })
        }
        return points;
    }

    static hasLonePairs(configuration: string): boolean {
        return this.hasPart(configuration, 1, "lp");
    }

    static hasPositions(configuration: string): boolean {
        return this.hasPart(configuration, 4, "p");
    }

    private static hasPart(configuration: string, part: number, label: string): boolean {
        const parts = configuration.split('|');
        if (this.isExtendedConfiguration(configuration)) {
            return parts.some(part => {
                const subParts = part.split("*");
                return subParts.some(subPart => subPart.toLowerCase() === label);
            })
        } else {
            return parts.length > part && parts[part].length > 0;
        }
    }

}
